const CART_DRAWER_ID = 'cart-drawer';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HanfiCartRecommendations extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    this.querySelector('[data-hanfi-cart-slider-prev]')?.addEventListener('click', () => this.#scroll(-1));
    this.querySelector('[data-hanfi-cart-slider-next]')?.addEventListener('click', () => this.#scroll(1));
    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen);

    if (this.closest(`#${CART_DRAWER_ID}`)?.hasAttribute('open') || this.classList.contains('hanfi-cart-recommendations--empty')) {
      this.load();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  #handleDrawerOpen = (event) => {
    if (event.target?.id === CART_DRAWER_ID && event.target.contains(this)) this.load();
  };

  #scroll(direction) {
    const list = this.querySelector('[data-hanfi-cart-recommendations-list]');
    if (!(list instanceof HTMLElement)) return;
    list.scrollBy({ left: direction * Math.max(180, list.clientWidth * 0.72), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  async load() {
    const url = this.dataset.url;
    if (this.#loaded || !url) return;
    this.#loaded = true;

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-hanfi-cart-recommendations-list]');
      const current = this.querySelector('[data-hanfi-cart-recommendations-list]');
      if (incoming?.children.length && current) current.innerHTML = incoming.innerHTML;
    } catch (error) {
      console.warn('[hanfi-cart] Recommendations failed:', error);
    }
  }
}

if (!customElements.get('hanfi-cart-recommendations')) {
  customElements.define('hanfi-cart-recommendations', HanfiCartRecommendations);
}

const parseTimerTarget = (value, repeatDaily) => {
  if (!value) return null;
  const now = new Date();

  if (repeatDaily && /^\d{1,2}:\d{2}$/.test(value)) {
    const [hours, minutes] = value.split(':').map(Number);
    const target = new Date(now);
    target.setHours(hours, minutes, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    return target;
  }

  const target = new Date(value);
  return Number.isNaN(target.getTime()) ? null : target;
};

const updateCountdown = (timer) => {
  const target = parseTimerTarget(timer.dataset.end, timer.dataset.repeatDaily === 'true');
  if (!target) return;

  const remaining = target.getTime() - Date.now();
  if (remaining <= 0) {
    timer.textContent = timer.dataset.completeText || 'Offer ended';
    timer.toggleAttribute('hidden', timer.dataset.hideComplete === 'true');
    return;
  }

  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (number) => String(number).padStart(2, '0');
  timer.textContent = days > 0
    ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
    : `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
};

const mountCountdowns = () => {
  document.querySelectorAll('[data-hanfi-countdown]').forEach((timer) => {
    if (!(timer instanceof HTMLElement) || timer.dataset.bound === 'true') return;
    timer.dataset.bound = 'true';
    updateCountdown(timer);
    window.setInterval(() => updateCountdown(timer), 1000);
  });
};

const mountAnnouncementSlides = () => {
  document.querySelectorAll('[data-hanfi-cart-slides]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.bound === 'true') return;
    const slides = Array.from(root.querySelectorAll('[data-hanfi-cart-slide]')).filter((slide) => slide instanceof HTMLElement);
    if (slides.length <= 1) return;

    root.dataset.bound = 'true';
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const activate = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
    };

    root.querySelector('[data-hanfi-cart-slide-prev]')?.addEventListener('click', () => activate(index - 1));
    root.querySelector('[data-hanfi-cart-slide-next]')?.addEventListener('click', () => activate(index + 1));

    if (!prefersReducedMotion()) {
      window.setInterval(() => activate(index + 1), 3600);
    }
  });
};

const burstConfetti = () => {
  if (prefersReducedMotion()) return;
  const root = document.createElement('div');
  root.className = 'hanfi-cart-confetti';
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return;

  root.appendChild(canvas);
  document.body.appendChild(root);

  const colors = ['#A94A6C', '#C7648A', '#F2D48A', '#F7EFE6', '#2A2326', '#FFFFFF'];
  const start = performance.now();
  const duration = 1650;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const resize = () => {
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  resize();
  window.addEventListener('resize', resize, { passive: true });

  const origins = [
    { x: window.innerWidth * 0.32, y: window.innerHeight * 0.2 },
    { x: window.innerWidth * 0.68, y: window.innerHeight * 0.2 },
  ];
  const pieces = Array.from({ length: 118 }, (_, index) => {
    const origin = origins[index % origins.length];
    const angle = (-92 + (Math.random() - 0.5) * 72) * (Math.PI / 180);
    const speed = 5.5 + Math.random() * 8.5;
    return {
      x: origin.x + (Math.random() - 0.5) * 44,
      y: origin.y + (Math.random() - 0.5) * 18,
      vx: Math.cos(angle) * speed + (index % 2 ? -2.2 : 2.2),
      vy: Math.sin(angle) * speed,
      gravity: 0.17 + Math.random() * 0.12,
      drag: 0.985 + Math.random() * 0.008,
      width: 5 + Math.random() * 8,
      height: 8 + Math.random() * 16,
      color: colors[index % colors.length],
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.38,
      shape: index % 5,
    };
  });

  const drawPiece = (piece, opacity) => {
    context.save();
    context.translate(piece.x, piece.y);
    context.rotate(piece.rotation);
    context.globalAlpha = opacity;
    context.fillStyle = piece.color;

    if (piece.shape === 0) {
      context.beginPath();
      context.arc(0, 0, piece.width * 0.58, 0, Math.PI * 2);
      context.fill();
    } else if (piece.shape === 1) {
      context.fillRect(-piece.width / 2, -piece.height / 2, piece.width, piece.height);
    } else {
      context.beginPath();
      context.moveTo(-piece.width / 2, -piece.height / 2);
      context.bezierCurveTo(piece.width, -piece.height / 3, -piece.width, piece.height / 3, piece.width / 2, piece.height / 2);
      context.lineTo(-piece.width / 2, piece.height / 2);
      context.closePath();
      context.fill();
    }

    context.restore();
  };

  const tick = (time) => {
    const elapsed = time - start;
    const progress = Math.min(elapsed / duration, 1);
    const opacity = progress < 0.72 ? 1 : Math.max(0, (1 - progress) / 0.28);

    context.clearRect(0, 0, window.innerWidth, window.innerHeight);
    pieces.forEach((piece) => {
      piece.vx *= piece.drag;
      piece.vy = piece.vy * piece.drag + piece.gravity;
      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.rotation += piece.spin;
      drawPiece(piece, opacity);
    });

    if (progress < 1) {
      window.requestAnimationFrame(tick);
    } else {
      window.removeEventListener('resize', resize);
      root.remove();
    }
  };

  window.requestAnimationFrame(tick);
};

const celebrateCompletedStates = () => {
  document.querySelectorAll('[data-hanfi-confetti]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const key = `hanfi-confetti:${node.dataset.hanfiConfetti}:${node.dataset.hanfiConfettiKey || ''}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, 'true');
    burstConfetti();
  });
};

let giftSubmitPending = false;

document.addEventListener('submit', (event) => {
  if (event.target instanceof HTMLFormElement && event.target.matches('.hanfi-cart-reward__gift-form')) {
    giftSubmitPending = true;
  }
}, true);

document.addEventListener('hanfi:cart-added', () => {
  if (!giftSubmitPending) return;
  giftSubmitPending = false;
  burstConfetti();
});

const addFreeGift = async (marker) => {
  if (!(marker instanceof HTMLElement) || marker.dataset.adding === 'true') return;
  const variantId = Number(marker.dataset.variantId);
  if (!variantId) return;

  const key = `hanfi-free-gift:${variantId}`;
  if (sessionStorage.getItem(key)) return;

  marker.dataset.adding = 'true';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`.replace(/([^:]\/)\/+/g, '$1'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _hanfi_free_gift: 'true',
            },
          },
        ],
      }),
    });

    if (!response.ok) return;
    sessionStorage.setItem(key, 'true');
    burstConfetti();
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('hanfi:free-gift-added', { bubbles: true }));
  } catch (error) {
    console.warn('[hanfi-cart] Free gift auto-add failed:', error);
  } finally {
    marker.dataset.adding = 'false';
  }
};

const mountAutoFreeGifts = () => {
  document.querySelectorAll('[data-hanfi-auto-free-gift]').forEach(addFreeGift);
};

const observer = new MutationObserver(() => {
  mountCountdowns();
  mountAnnouncementSlides();
  celebrateCompletedStates();
  mountAutoFreeGifts();
});

mountCountdowns();
mountAnnouncementSlides();
celebrateCompletedStates();
mountAutoFreeGifts();
observer.observe(document.documentElement, { childList: true, subtree: true });
